import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"

import { css } from "@emotion/react"
import { rgba } from "polished"
import { useBreakpoint } from "gatsby-plugin-breakpoints"
import Modal from "react-modal"
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import ReactPlayer from "react-player"

import useMixins, { fontSize, globalColors } from "../css-functions"
import modalStyle from "../../components/modal-style"
import Image from "../image"

import SvgCloseButton from "../../assets/svg/x.svg"

import JSONData from "../../../content/works.json"

const WorksVideos = ({
  pcTitle,
}) => {
  const [ modalIsOpen, setIsOpen ] = useState(false)
  const [ videoURL, setVideoURL ] = useState("")
  const [ targetModalElement, setModalElement ] = useState(null)

  const bp = useBreakpoint()
  const mixins = useMixins()

  const handleOpenModal = id => {
    setVideoURL(JSONData.works.videos[id].url)
    setIsOpen(true)
  }

  const handleCloseModal = () => {
    setVideoURL("")
    setIsOpen(false)
  }

  useEffect(() => {
    if ( targetModalElement !== null ) {
      modalIsOpen ? disableBodyScroll(targetModalElement) : enableBodyScroll(targetModalElement)
    }

    return () => clearAllBodyScrollLocks()
  }, [ modalIsOpen, targetModalElement ])

  const overriddenModalStyle = modalStyle({
    backgroundColor: rgba(globalColors.black, 0.8),
  }, {
    padding: 0,
    top: `50%`,
    right: `unset`,
    bottom: `unset`,
    left: `50%`,
    transform: `translate( -50%, -50% )`,
    border: `none`,
    borderRadius: 0,
    backgroundColor: rgba(globalColors.black, 0.8),
    overflow: `visible`,
  })

  const playButtonStyle = {
    titleSize: `${ bp.isMiddle ? bp.isTablet ? bp.isPc ? fontSize(16) : fontSize(14) : fontSize(16) : fontSize(14) }`,
    titleOffset: `${ bp.isMiddle ? bp.isTablet? bp.isPc ? '80px' : '50px' : '80px' : '50px' }`,
    circle: `${ bp.isMiddle ? bp.isTablet? bp.isPc ? '100px' : '60px' : '100px' : '60px' }`,
    triangle: {
      topBottom: `${ bp.isMiddle ? bp.isTablet ? bp.isPc ? '20px' : '12px' : '20px' : '12px' }`,
      rightLeft: `${ bp.isMiddle ? bp.isTablet ? bp.isPc ? '32px' : '22px' : '32px' : '22px' }`,
    },
  }

  const style = {
    leadWrapper: css`
      ${mixins.default.leadWrapperStyle};
    `,
    text: css`
      ${mixins.default.textStyle}
    `,
    list: css`
      display: grid;
      row-gap: 60px;
      column-gap: ${ bp.isTablet ? bp.isPc ? '48px' : '24px' : '' };
      grid-template-columns: ${ bp.isTablet ? bp.isPc ? 'repeat( 2, 480px )' : '1fr 1fr' : '' };
      transform: ${ bp.isPc ? 'translateX( calc( ( 640px - 16px * 2 - ( 480px * 2 + 48px ) ) / 2 ) )' : '' };
    `,
    listItem: css`
      width: 100%;
      padding-top: 56.25%;
      position: relative;
    `,
    thumbnail: css`
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      filter: brightness( 0.5 );
    `,
    title: css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate( -50%, calc( -50% - ${ playButtonStyle.titleOffset } ) );
      ${ playButtonStyle.titleSize }
      color: ${ globalColors.white };
      font-weight: 700;
      letter-spacing: 0.1em;
    `,
    overlayButton: css`
      width: 100%;
      height: 100%;
      appearance: none;
      position: absolute;
      top: 0;
      left: 0;
      cursor: pointer;
      text-indent: -9999px;
      &::before,
      &::after {
        content: "";
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate( -50%, -50% );
        z-index: 1;
      }
      &::before {
        width: ${ playButtonStyle.circle };
        height: ${ playButtonStyle.circle };
        border: 1px solid ${ globalColors.white };
        border-radius: 50%;
        background-color: ${ rgba( globalColors.black, 0.5 ) };
      }
      &::after {
        border-top: ${ playButtonStyle.triangle.topBottom } solid transparent;
        border-right: ${ playButtonStyle.triangle.rightLeft } solid transparent;
        border-bottom: ${ playButtonStyle.triangle.topBottom } solid transparent;
        border-left: ${ playButtonStyle.triangle.rightLeft } solid ${ globalColors.white };
        transform: translate( calc( -25% + 3px ), -50% );
      }
    `,
    close: css`
      position: absolute;
      top: -32px;
      right: 0;
      cursor: pointer;
      filter: brightness(2.0) invert(0);
    `,
    videoWrapper: css`
      width: calc( 100vw - 32px );
      height: calc( ( 100vw - 32px ) * ( 9 / 16 ) );
      max-width: 1280px;
      max-height: 720px;
      overflow: hidden;
      position: relative;
    `,
    spinner: css`
      ${ mixins.default.loadingSpinner }
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate( -50%, calc( -50% - 2.5em ));
      z-index: 0;
    `,
  }

  return (
    <>
      { pcTitle }
      <div css={style.leadWrapper}>
        <p css={style.text}>　盆石を打つ所作を動画にご覧いただけます。盆石の魅力をご堪能ください。</p>
      </div>
      <ul css={style.list}>
        {
          JSONData.works.videos.map((item) => (
            <li
              key={ item.id }
              css={ style.listItem }
            >
              <figure css={ style.thumbnail }>
                <Image filename={ item.imgFile } />
              </figure>
              <figcaption css={ style.title }>
                { item.title }
              </figcaption>
              <button
                type="button"
                css={ style.overlayButton }
                onClick={ () => handleOpenModal(item.id) }
              >
                再生
              </button>
            </li>
          ))
        }
      </ul>
      <Modal
        contentRef={ node => setModalElement(node) }
        isOpen={ modalIsOpen }
        onRequestClose={ () => handleCloseModal() }
        shouldCloseOnOverlayClick={ true }
        style={ overriddenModalStyle }
      >
        <button
          type="button"
          onClick={ () => handleCloseModal() }
          css={ style.close }
        >
          <SvgCloseButton />
        </button>
        <div css={ style.videoWrapper }>
          <div css={ style.spinner } />
          <ReactPlayer
            url={ videoURL }
            width="100%"
            height="100%"
            style={{
              position: `relative`,
              zIndex : 1,
            }}
          />
        </div>
      </Modal>
    </>
  )
}

WorksVideos.propTypes = {
  pcTitle: PropTypes.object,
}

export default WorksVideos